import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AccountBalance, Box, withStyles } from 'vopay';
import { BodyText, Button, Card, Title } from 'UI';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { useToast } from '@/providers/Toast.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import AccountDetailsDrawer from './AccountDetailsDrawer/AccountDetailsDrawer';
import TransferDrawer from './Transfer/TransferDrawer';
import PartnerAccountCreation from './AccountCreation/PartnerAccountCreation/PartnerAccountCreation';
import AccountsOverview from './AccountsOverview/AccountsOverview';
import { getAccountStatusTiles } from './AccountsOverview/onboardingPipeline';
import useFetchPartnerAccountStatusSummary from '@/components/Entities/hooks/useApi/useFetchPartnerAccountStatusSummary';
import AccountFilters from './AccountFilters';
import AccountsTable from './AccountsTable';
import useAccountsListing from './useAccountsListing';
import * as accountPageRoute from './accountPage.route';
import * as accountCreationRoute from './AccountCreation/accountCreation.route';
import styles from './AccountsList.module.scss';

const cn = withStyles(styles);

type AccountsListProps = {
  onRouteAccess: () => void;
  /** Exports the current filters; an account name narrows the export to that one account. */
  exportCSV: (accountName?: string) => void;
};

export default function AccountsList({ onRouteAccess, exportCSV }: AccountsListProps) {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.List' });
  const { t: tOverview } = useTranslation('translations', { keyPrefix: 'Accounts.AccountsOverview' });
  const toast = useToast();

  const {
    response: { isRoleViewOnly, isInsideSales, partnerTypeLabel },
  } = usePartnerData();

  const { permissions } = usePermissions();

  const { canCreateAccount } = useModuleFeatureFlag('accounts');
  const canTransfer = permissions.AccountTransfersEnabled;

  const listing = useAccountsListing({
    onLoadError: (error) => {
      // Without this the failure is indistinguishable from "this partner has no accounts". The hook
      // has already logged the cause; the user needs to know the list is wrong, not just empty, and
      // the reason it gives support something to act on.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(t('loadError', { defaultValue: 'Could not load your accounts: {{errorMessage}}', errorMessage }));
    },
  });

  // The status summary feeds the overview's stage tiles and, through them, the Status filter's
  // options — one list for both (ID-13964 D3), so it is fetched here rather than inside the overview.
  const { fetchingStatusSummary, numTotalAccounts, statusCounts, fetchPartnerAccountStatusSummary } =
    useFetchPartnerAccountStatusSummary();

  // The hook's `fetching` is false until the mount effect below runs; without this the overview would
  // flash zeroed totals for one frame first.
  const [hasLoadedStatusSummary, setHasLoadedStatusSummary] = useState(false);

  // Fetched once on mount; the summary is independent of the list's filters and pagination.
  useEffect(() => {
    fetchPartnerAccountStatusSummary()
      .catch((error) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        toast.neutral(
          tOverview('loadError', {
            defaultValue: 'Could not load the accounts overview: {{errorMessage}}',
            errorMessage,
          }),
        );
      })
      .finally(() => setHasLoadedStatusSummary(true));
  }, []);

  const statusTiles = useMemo(
    () => getAccountStatusTiles(partnerTypeLabel, permissions.partnerLiability, statusCounts),
    [partnerTypeLabel, permissions.partnerLiability, statusCounts],
  );

  const { filters } = listing;

  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountBalance | null>(null);
  // Null keeps the transfer drawer closed; a row's Transfer button names the account to debit, the
  // header's starts blank.
  const [transferRequest, setTransferRequest] = useState<{ fromAccount?: AccountBalance } | null>(null);

  useEffect(() => {
    onRouteAccess();
  }, [location]);

  return (
    <Box flx="col" gapSize="36px">
      <Box jc="sb">
        {/* Figma: H1/Large Title 32/Emphasized + Body/16/Regular (Text/Secondary75) */}
        <div className={cn('pageHeading')}>
          <Title>{t('title', { defaultValue: 'Accounts' })}</Title>

          <BodyText className={cn('pageDescription')}>
            {t('pageDescription', {
              defaultValue: 'All accounts and sub-accounts in your book, created by you or your partners.',
            })}
          </BodyText>
        </div>

        {/* Figma `Buttons`: Size="Large"; Export CSV Type="Secondary", Add Account Type="Primary" + Left Indicator="add" */}
        <div className={cn('headerActions')}>
          <Button
            id="accounts-ExportCSV"
            size="large"
            disabled={isRoleViewOnly}
            onClick={() => {
              exportCSV();
            }}
          >
            {t('exportCSVButton', { defaultValue: 'Export CSV' })}
          </Button>

          {canTransfer && (
            <Button
              id="accounts-transfer"
              size="large"
              disabled={isRoleViewOnly}
              onClick={() => setTransferRequest({})}
            >
              {t('transferButton', { defaultValue: 'Transfer' })}
            </Button>
          )}

          {canCreateAccount && (
            <Button
              id="accounts-addAccount"
              colorScheme="primary"
              size="large"
              iconLeft="add"
              disabled={isRoleViewOnly}
              onClick={() =>
                isInsideSales ? history.push(accountCreationRoute.getPath()) : setIsCreateDrawerOpen(true)
              }
            >
              {t('addAccountButton', { defaultValue: 'Add Account' })}
            </Button>
          )}
        </div>
      </Box>

      <AccountsOverview
        // The listing carries the partner's program terms, so the card reads them off its first answer.
        fetchingProgramTerms={!listing.programTerms && !listing.hasLoaded}
        revShare={listing.programTerms?.RevShare ?? null}
        minimumProgramFee={listing.programTerms?.MinimumProgramFee ?? null}
        fetchingStatusSummary={fetchingStatusSummary || !hasLoadedStatusSummary}
        numTotalAccounts={numTotalAccounts}
        statusCounts={statusCounts}
        stages={statusTiles}
        selectedStatus={filters.status}
        // A tile is the Status filter set to that status, and clicking the selected one again clears it
        // (ID-13981); the other filters stay as they are.
        onStatusClick={(clickedStatus) =>
          listing.applyFilters({
            Country: filters.country,
            Currency: filters.currency,
            SearchText: filters.searchText,
            VopayAccountStatus: clickedStatus === filters.status ? '' : clickedStatus,
          })
        }
      />

      <Card className={cn('listCard')}>
        <AccountFilters listing={listing} statuses={statusTiles} />

        <AccountsTable
          listing={listing}
          hasPartnerColumn={permissions.PartnerCreationEnabled}
          onAccountClick={setSelectedAccount}
          onTransferClick={canTransfer ? (account) => setTransferRequest({ fromAccount: account }) : undefined}
        />
      </Card>

      {isCreateDrawerOpen && (
        <PartnerAccountCreation onClose={() => setIsCreateDrawerOpen(false)} onCreated={listing.loadAccounts} />
      )}

      <AccountDetailsDrawer
        account={selectedAccount}
        onClose={() => setSelectedAccount(null)}
        onViewFullDetails={(account) =>
          history.push(accountPageRoute.getPath({ accountID: account.AccountID, currency: account.Currency }))
        }
      />

      <TransferDrawer
        isOpen={transferRequest !== null}
        fromAccount={transferRequest?.fromAccount}
        onClose={() => setTransferRequest(null)}
        onTransferred={() => {
          setTransferRequest(null);
          listing.loadAccounts();
        }}
      />
    </Box>
  );
}
